"use client";

import { useEffect, useState } from "react";
import { limitsApi, type ClientLimitType, type ClientLimitsStatusResponse } from "@/api/limits";
import { isCallBlockedByLimitsStatus } from "@/utils/callLimits";
import { useAuth } from "@/context/AuthContext";
import { UserType } from "@/types/AuthTypes";
import { hasAllPermissions } from "@/utils/roleChecks";
import isPermitted from "@/utils/isPermitted";
import { PAGE_ROUTES } from "@/constants/PermissionsConstants";

type UseCallLimitsStatusResult = {
  isLoadingLimits: boolean;
  isCallBlockedByLimits: boolean;
  isCallStartBlocked: boolean;
  limitTypes: ClientLimitType[];
  limitsStatusData: ClientLimitsStatusResponse | null;
};

export default function useCallLimitsStatus(
  clientId?: number | null,
): UseCallLimitsStatusResult {
  const { user, allPermissions } = useAuth();
  const skipBlocking = hasAllPermissions(user?.sf_role);
  // Both endpoints below require view_client_limits. Asking anyway produced two
  // 403s in the console on every page of the app (this hook runs in AppHeader),
  // and the answer was unusable regardless — the catch below already fell back
  // to "not blocked".
  const canViewLimits = isPermitted(
    user as UserType,
    PAGE_ROUTES.CLIENT_PERMISSIONS.VIEW_CLIENT_LIMITS,
    allPermissions,
  );
  const [isLoadingLimits, setIsLoadingLimits] = useState(false);
  const [isCallBlockedByLimits, setIsCallBlockedByLimits] = useState(false);
  const [limitTypes, setLimitTypes] = useState<ClientLimitType[]>([]);
  const [limitsStatusData, setLimitsStatusData] =
    useState<ClientLimitsStatusResponse | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    const fetchCallLimitsStatus = async () => {
      if (!clientId || !canViewLimits) {
        setIsCallBlockedByLimits(false);
        setIsLoadingLimits(false);
        setLimitTypes([]);
        setLimitsStatusData(null);
        return;
      }

      try {
        setIsLoadingLimits(true);
        const [typesResponse, statusResponse] = await Promise.all([
          limitsApi.getLimitTypes(),
          limitsApi.getClientLimitsStatus(clientId, controller.signal),
        ]);
        if (controller.signal.aborted) return;

        setLimitTypes(typesResponse.data ?? []);
        setLimitsStatusData(statusResponse.data ?? null);
        setIsCallBlockedByLimits(
          skipBlocking
            ? false
            : isCallBlockedByLimitsStatus(statusResponse.data ?? null),
        );
      } catch {
        if (!controller.signal.aborted) {
          setIsCallBlockedByLimits(false);
          setLimitTypes([]);
          setLimitsStatusData(null);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoadingLimits(false);
        }
      }
    };

    fetchCallLimitsStatus();
    return () => controller.abort();
  }, [clientId, skipBlocking, canViewLimits]);

  const isCallStartBlocked = skipBlocking
    ? false
    : isLoadingLimits || isCallBlockedByLimits;

  return {
    isLoadingLimits,
    isCallBlockedByLimits,
    isCallStartBlocked,
    limitTypes,
    limitsStatusData,
  };
}

